import { validateFiles } from '~/modules/file';
import {
    CONVERTIBLE_IMAGE_EXTENSIONS,
    GIF_IMAGE_EXTENSIONS,
} from '@modules/file/config/image-upload';

/**
 * The maximum number of attachments for chat messages. This is equal to
 * `MaxNumEnquiryAttachments` in `PageController.php`.
 */
export const MAX_NUM_ATTACHMENTS = 10;
/**
 * Allowed image file extensions. Used for chat thumbnail uploads.
 * Includes animated formats (gif, apng), plus all convertible image formats.
 */
const ALLOWED_IMAGE_EXTENSIONS = [
    ...CONVERTIBLE_IMAGE_EXTENSIONS,
    ...GIF_IMAGE_EXTENSIONS,
] as const;
/**
 * Allowed file extensions for attachments for chat messages. Must match values
 * in `ALLOWED_FILES_ENQUIRY_INBOX` in
 * `themes/archipro2/javascript/react/helpers/constants.ts`.
 */
const ALLOWED_FILE_EXTENSIONS = [
    ...ALLOWED_IMAGE_EXTENSIONS,
    'pdf',
    'doc',
    'docx',
    'txt',
    'xls',
    'xlsx',
] as const;

/**
 * Validate a file for chat message attachments. Validates the following properties:
 * - File size (different for image files).
 * - File extension.
 * @param file The file to validate.
 * @returns The validation result.
 */
export function validateChatUploadFile(file: File) {
    return validateFiles({
        files: [file],
        validationOptions: { fileExtensions: ALLOWED_FILE_EXTENSIONS },
    });
}

/**
 * Validate the number of files for chat message attachments.
 * @param fileCount The number of files to validate.
 * @returns An error message if the number of files is greater than the maximum
 *  number of files allowed.
 */
export function validateChatUploadFileCount(fileCount: number) {
    if (fileCount > MAX_NUM_ATTACHMENTS) {
        return {
            message: `You can upload up to ${MAX_NUM_ATTACHMENTS} files at once`,
        };
    }
    return true;
}

/**
 * Validate a file for chat thumbnail uploads. Validates the following properties:
 * - File size.
 * - File extension.
 * @param file The file to validate.
 * @returns The validation result.
 */
export function validateChatThumbnailUploadFile(file: File) {
    return validateFiles({
        files: [file],
        validationOptions: { fileExtensions: ALLOWED_IMAGE_EXTENSIONS },
    });
}
